/**
 * ENJOY JAPAN Theme - Main JavaScript
 */

// Swiper
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

/**
 * ヒーロースライダーの初期化
 */
function initHeroSlider() {
  const swiperElement = document.querySelector('.p-hero__swiper');
  if (!swiperElement) return;

  new Swiper('.p-hero__swiper', {
    modules: [Navigation, Pagination],
    loop: true,
    slidesPerView: 1,
    spaceBetween: 0,
    navigation: {
      prevEl: '.p-hero__swiper-prev',
      nextEl: '.p-hero__swiper-next',
    },
    pagination: {
      el: '.p-hero__swiper-pagination',
      clickable: true,
    },
  });
}

/**
 * 成功事例一覧ページのHeroスライダー初期化
 */
function initCaseStudyHeroSlider() {
  const swiperElement = document.querySelector('.p-case-study-hero__swiper');
  if (!swiperElement) return;

  new Swiper('.p-case-study-hero__swiper', {
    modules: [Navigation, Pagination],
    loop: true,
    slidesPerView: 1,
    spaceBetween: 0,
    navigation: {
      prevEl: '.p-case-study-hero__nav--prev',
      nextEl: '.p-case-study-hero__nav--next',
    },
    pagination: {
      el: '.p-case-study-hero__pagination',
      clickable: true,
    },
  });
}

/**
 * コラム一覧ページの人気記事スライダー初期化
 */
function initColumnPopularSlider() {
  const swiperElement = document.querySelector('.p-column-popular__swiper');
  if (!swiperElement) return;

  new Swiper('.p-column-popular__swiper', {
    modules: [Pagination],
    loop: false,
    slidesPerView: 1,
    spaceBetween: 12,
    pagination: {
      el: '.p-column-popular__swiper-pagination',
      clickable: true,
    },
    breakpoints: {
      768: {
        slidesPerView: 3,
        spaceBetween: 24,
      },
    },
  });
}

/**
 * 事例セクションのタブフィルター（トップページ用）
 */
function initCaseStudiesFilter() {
  const filterContainer = document.querySelector('.p-case-studies__tabs');
  if (!filterContainer) return;

  const tabs = filterContainer.querySelectorAll('.p-case-studies__tab');
  const cards = document.querySelectorAll('.p-case-studies__card');
  const emptyMessage = document.querySelector('.p-case-studies__empty');

  /**
   * 対象: トップページ「成功事例」セクション
   * 用途: タブフィルター後も含めた最大表示件数
   */
  const MAX_CARDS = window.ejConfig?.topCaseStudiesMax || 6;

  // 初期表示時：地域未設定の記事を非表示 + 6件までに制限
  const validCards = Array.from(cards).filter((card) => card.dataset.region);
  const visibleCards = new Set(validCards.slice(0, MAX_CARDS));

  cards.forEach((card) => {
    card.classList.toggle('is-hidden', !visibleCards.has(card));
  });

  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const filter = tab.dataset.filter;

      // タブの状態更新
      tabs.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-pressed', 'false');
      });
      tab.classList.add('is-active');
      tab.setAttribute('aria-pressed', 'true');

      // カードのフィルタリング
      let visibleCount = 0;
      cards.forEach((card) => {
        const region = card.dataset.region;
        // 地域が未設定の記事は非表示
        const isMatch = region && (filter === 'all' || region === filter || region === 'worldwide');

        if (isMatch && visibleCount < MAX_CARDS) {
          card.classList.remove('is-hidden');
          visibleCount++;
        } else {
          card.classList.add('is-hidden');
        }
      });

      // 表示する記事がない場合はメッセージを表示
      if (emptyMessage) {
        if (visibleCount === 0) {
          emptyMessage.classList.add('is-visible');
        } else {
          emptyMessage.classList.remove('is-visible');
        }
      }
    });
  });
}

/**
 * 成功事例一覧ページのタブフィルター＋ページネーション
 */
function initCaseStudyListFilter() {
  const container = document.querySelector('.p-case-study-list');
  if (!container) return;

  const tabs = container.querySelectorAll('.p-case-study-list__tab');
  const cards = container.querySelectorAll('.p-case-study-list__card');
  const pagination = container.querySelector('.c-pagination');
  const emptyMessage = container.querySelector('.p-case-study-list__empty');

  /**
   * 対象: 成功事例一覧ページ（/case-study/）
   * 用途: ページネーションの1ページあたりの件数
   */
  const ITEMS_PER_PAGE = Number(window.ejConfig?.caseStudyListPerPage) || 12;
  let currentPage = 1;
  let currentFilter = 'all';

  // フィルタリング済みカードを取得
  function getFilteredCards() {
    return Array.from(cards).filter((card) => {
      const region = card.dataset.region;
      // 地域が未設定の記事は非表示
      return region && (currentFilter === 'all' || region === currentFilter || region === 'worldwide');
    });
  }

  // ページネーションHTML生成
  function renderPagination(totalPages) {
    if (totalPages <= 1) {
      pagination.classList.add('is-hidden');
      return;
    }

    pagination.classList.remove('is-hidden');
    let html = '';

    // 前へボタン
    html += `<button class="c-pagination__nav c-pagination__nav--prev" ${currentPage === 1 ? 'disabled' : ''} data-page="${currentPage - 1}">
      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`;

    // ページ番号ボタン
    const pages = getPaginationRange(currentPage, totalPages);
    pages.forEach((page) => {
      if (page === '...') {
        html += '<span class="c-pagination__ellipsis">...</span>';
      } else {
        html += `<button class="c-pagination__button ${page === currentPage ? 'is-active' : ''}" data-page="${page}">${page}</button>`;
      }
    });

    // 次へボタン
    html += `<button class="c-pagination__nav c-pagination__nav--next" ${currentPage === totalPages ? 'disabled' : ''} data-page="${currentPage + 1}">
      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`;

    pagination.innerHTML = html;

    // ページネーションボタンのイベントリスナー
    pagination.querySelectorAll('[data-page]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const page = parseInt(btn.dataset.page, 10);
        if (page >= 1 && page <= totalPages) {
          currentPage = page;
          updateDisplay();
          // ページ切り替え時にスクロール
          container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // ページ番号範囲を取得（省略記号付き）
  function getPaginationRange(current, total) {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    let prev = null;
    for (const i of range) {
      if (prev !== null) {
        if (i - prev === 2) {
          rangeWithDots.push(prev + 1);
        } else if (i - prev !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      prev = i;
    }

    return rangeWithDots;
  }

  // 表示更新
  function updateDisplay() {
    const filtered = getFilteredCards();
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;

    // すべて非表示にしてから、該当ページのものだけ表示
    cards.forEach((card) => card.classList.add('is-hidden'));
    filtered.slice(start, end).forEach((card) => card.classList.remove('is-hidden'));

    // 空状態
    if (filtered.length === 0) {
      emptyMessage.classList.add('is-visible');
    } else {
      emptyMessage.classList.remove('is-visible');
    }

    // ページネーション更新
    renderPagination(totalPages);
  }

  // タブクリックイベント
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const filter = tab.dataset.filter;

      // タブの状態更新
      tabs.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-pressed', 'false');
      });
      tab.classList.add('is-active');
      tab.setAttribute('aria-pressed', 'true');

      // フィルター更新＆ページリセット
      currentFilter = filter;
      currentPage = 1;
      updateDisplay();
    });
  });

  // 初期表示
  updateDisplay();
}

/**
 * コラム一覧ページネーションの初期化
 */
function initColumnPagination() {
  const grid = document.querySelector('[data-column-grid]');
  const pagination = document.querySelector('[data-column-pagination]');
  if (!grid || !pagination) return;

  const cards = Array.from(grid.querySelectorAll('[data-column-card]'));

  /**
   * 対象: コラム一覧ページ（/column/）
   * 用途: ページネーションの1ページあたりの件数
   */
  const ITEMS_PER_PAGE = Number(window.ejConfig?.columnListPerPage) || 12;
  let currentPage = 1;

  // ページ番号範囲を取得（省略記号付き）
  function getPaginationRange(current, total) {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    let prev = null;
    for (const i of range) {
      if (prev !== null) {
        if (i - prev === 2) {
          rangeWithDots.push(prev + 1);
        } else if (i - prev !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      prev = i;
    }

    return rangeWithDots;
  }

  // ページネーションHTML生成
  function renderPagination(totalPages) {
    if (totalPages <= 1) {
      pagination.classList.add('is-hidden');
      return;
    }

    pagination.classList.remove('is-hidden');
    let html = '';

    // 前へボタン
    html += `<button class="c-pagination__nav c-pagination__nav--prev" ${currentPage === 1 ? 'disabled' : ''} data-page="${currentPage - 1}">
      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`;

    // ページ番号ボタン
    const pages = getPaginationRange(currentPage, totalPages);
    pages.forEach((page) => {
      if (page === '...') {
        html += '<span class="c-pagination__ellipsis">...</span>';
      } else {
        html += `<button class="c-pagination__button ${page === currentPage ? 'is-active' : ''}" data-page="${page}">${page}</button>`;
      }
    });

    // 次へボタン
    html += `<button class="c-pagination__nav c-pagination__nav--next" ${currentPage === totalPages ? 'disabled' : ''} data-page="${currentPage + 1}">
      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`;

    pagination.innerHTML = html;

    // ページネーションボタンのイベントリスナー
    pagination.querySelectorAll('[data-page]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const page = parseInt(btn.dataset.page, 10);
        const totalPages = Math.ceil(cards.length / ITEMS_PER_PAGE);
        if (page >= 1 && page <= totalPages) {
          currentPage = page;
          updateDisplay();
          // ページ切り替え時にスクロール
          grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // 表示更新
  function updateDisplay() {
    const totalPages = Math.ceil(cards.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    // すべて非表示にしてから、該当ページのみ表示
    cards.forEach((card) => {
      card.style.display = 'none';
    });
    cards.slice(startIndex, endIndex).forEach((card) => {
      card.style.display = '';
    });

    // ページネーション更新
    renderPagination(totalPages);
  }

  // 初期表示
  updateDisplay();
}

/**
 * オーバーレイナビゲーション開閉
 */
function initOverlayNav() {
  const menuBtn = document.querySelector('.l-header__menu-btn');
  const overlayNav = document.getElementById('overlay-nav');

  if (!menuBtn || !overlayNav) return;

  const openMenu = () => {
    overlayNav.classList.add('is-open');
    overlayNav.setAttribute('aria-hidden', 'false');
    menuBtn.classList.add('is-open');
    menuBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  };

  const closeMenu = () => {
    overlayNav.classList.remove('is-open');
    overlayNav.setAttribute('aria-hidden', 'true');
    menuBtn.classList.remove('is-open');
    menuBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    // アコーディオンをリセット
    document.querySelectorAll('[data-accordion].is-open').forEach((group) => {
      group.classList.remove('is-open');
      const toggle = group.querySelector('.l-overlay-nav__accordion-toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  };

  // メニューボタンクリック（トグル動作）
  menuBtn.addEventListener('click', () => {
    if (overlayNav.classList.contains('is-open')) {
      closeMenu();
    } else {
      openMenu();
    }
  });

  // ESCキーで閉じる
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlayNav.classList.contains('is-open')) {
      closeMenu();
    }
  });
}

/**
 * SPアコーディオン（オーバーレイナビゲーション内）
 */
function initAccordion() {
  const accordionGroups = document.querySelectorAll('[data-accordion]');

  accordionGroups.forEach((group) => {
    const toggle = group.querySelector('.l-overlay-nav__accordion-toggle');
    if (!toggle) return;

    toggle.addEventListener('click', (e) => {
      e.preventDefault();

      // SPのみ動作（768px未満）
      if (window.innerWidth >= 768) return;

      const isOpen = group.classList.contains('is-open');

      if (isOpen) {
        group.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        group.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

/**
 * お知らせ一覧ページネーションの初期化
 */
function initNewsPagination() {
  const grid = document.querySelector('[data-news-grid]');
  const pagination = document.querySelector('[data-news-pagination]');
  if (!grid || !pagination) return;

  const items = Array.from(grid.querySelectorAll('[data-news-item]'));
  const emptyMessage = document.querySelector('.p-news-list__empty');

  // 投稿がない場合は終了
  if (items.length === 0) return;

  /**
   * 対象: お知らせ一覧ページ（/category/news/）
   * 用途: ページネーションの1ページあたりの件数
   */
  const ITEMS_PER_PAGE = Number(window.ejConfig?.newsListPerPage) || 8;
  let currentPage = 1;

  // ページ番号範囲を取得（省略記号付き）
  function getPaginationRange(current, total) {
    const delta = 1;
    const range = [];
    const rangeWithDots = [];

    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      }
    }

    let prev = null;
    for (const i of range) {
      if (prev !== null) {
        if (i - prev === 2) {
          rangeWithDots.push(prev + 1);
        } else if (i - prev !== 1) {
          rangeWithDots.push('...');
        }
      }
      rangeWithDots.push(i);
      prev = i;
    }

    return rangeWithDots;
  }

  // ページネーションHTML生成
  function renderPagination(totalPages) {
    if (totalPages <= 1) {
      pagination.classList.add('is-hidden');
      return;
    }

    pagination.classList.remove('is-hidden');
    let html = '';

    // 前へボタン
    html += `<button class="c-pagination__nav c-pagination__nav--prev" ${currentPage === 1 ? 'disabled' : ''} data-page="${currentPage - 1}">
      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`;

    // ページ番号ボタン
    const pages = getPaginationRange(currentPage, totalPages);
    pages.forEach((page) => {
      if (page === '...') {
        html += '<span class="c-pagination__ellipsis">...</span>';
      } else {
        html += `<button class="c-pagination__button ${page === currentPage ? 'is-active' : ''}" data-page="${page}">${page}</button>`;
      }
    });

    // 次へボタン
    html += `<button class="c-pagination__nav c-pagination__nav--next" ${currentPage === totalPages ? 'disabled' : ''} data-page="${currentPage + 1}">
      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M1.5 1L6.5 6L1.5 11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>`;

    pagination.innerHTML = html;

    // ページネーションボタンのイベントリスナー
    pagination.querySelectorAll('[data-page]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const page = parseInt(btn.dataset.page, 10);
        const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
        if (page >= 1 && page <= totalPages) {
          currentPage = page;
          updateDisplay();
          // ページ切り替え時にスクロール
          grid.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  // 表示更新
  function updateDisplay() {
    const totalPages = Math.ceil(items.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;

    // すべて非表示にしてから、該当ページのみ表示
    items.forEach((item) => {
      item.style.display = 'none';
    });
    items.slice(startIndex, endIndex).forEach((item) => {
      item.style.display = '';
    });

    // 空状態メッセージの非表示（投稿がある場合）
    if (emptyMessage) {
      emptyMessage.classList.remove('is-visible');
    }

    // ページネーション更新
    renderPagination(totalPages);
  }

  // 初期表示
  updateDisplay();
}

/**
 * 資料ダウンロードページのカテゴリーフィルター
 */
function initDocumentFilter() {
  const container = document.querySelector('.p-document-list');
  if (!container) return;

  const tabs = container.querySelectorAll('.p-document-list__filter-tab');
  const cards = container.querySelectorAll('.p-document-list__card');
  const emptyMessage = container.querySelector('[data-document-empty]');

  if (tabs.length === 0 || cards.length === 0) return;

  // タブクリック時のフィルタリング
  tabs.forEach((tab) => {
    tab.addEventListener('click', () => {
      const filter = tab.dataset.filter;

      // タブのアクティブ状態を更新
      tabs.forEach((t) => {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      tab.classList.add('is-active');
      tab.setAttribute('aria-selected', 'true');

      // カードのフィルタリング
      let visibleCount = 0;
      cards.forEach((card) => {
        const categories = card.dataset.category ? card.dataset.category.split(',') : [];

        if (filter === 'all' || categories.includes(filter)) {
          card.classList.remove('is-hidden');
          visibleCount++;
        } else {
          card.classList.add('is-hidden');
        }
      });

      // 空状態メッセージの表示切り替え
      if (emptyMessage) {
        if (visibleCount === 0) {
          emptyMessage.classList.add('is-visible');
        } else {
          emptyMessage.classList.remove('is-visible');
        }
      }
    });
  });
}

/**
 * DOMContentLoaded
 */
document.addEventListener('DOMContentLoaded', () => {
  // ヒーロースライダー
  initHeroSlider();

  // 成功事例一覧ページのHeroスライダー
  initCaseStudyHeroSlider();

  // コラム一覧ページの人気記事スライダー
  initColumnPopularSlider();

  // 事例セクションフィルター（トップページ）
  initCaseStudiesFilter();

  // 成功事例一覧ページのフィルター＋ページネーション
  initCaseStudyListFilter();

  // コラム一覧ページのページネーション
  initColumnPagination();

  // お知らせ一覧ページのページネーション
  initNewsPagination();

  // オーバーレイナビゲーション
  initOverlayNav();

  // SPアコーディオン
  initAccordion();

  // 資料ダウンロードページのフィルター
  initDocumentFilter();
});
